import React, { useEffect, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { getOtherProperties } from "../common/Utils";
import { setTransitionDuration } from "../common/Dom";
import { PickerItemProps } from "./PropsType";

export default function PickerItem(props: PickerItemProps) {
  const isMobile = "ontouchstart" in document;
  const wrapper = useRef(null);
  const itemHeight = useRef(0);
  const itemRef = useRef(null);
  const startY = useRef(0);
  const moveY = useRef(0);
  const dragging = useRef(false);
  const { data: dataProps = [], className, ...other }: any = props;
  const [state, setState] = useState({
    data: dataProps,
    activeIndex: 0,
    y: 0,
  });
  const styleClass = classNames("PickerItem", className);
  const otherProps: object = getOtherProperties(other, [
    "children",
    "format",
    "onChange",
    "onValueChange",
    "value",
  ]);
  const getMenusItem = useCallback(() => {
    let y: any = moveY.current;
    const data = state.data || [];

    const format: any =
      typeof props.format === "function"
        ? props.format
        : (label: any) => {
            return label;
          };
    y = Math.max(Math.min(y, 0), -itemHeight.current * (data.length - 1));
    const activeIndex: number = Math.floor(Math.abs(y / itemHeight.current));
    return data.map((item: any, index: number) => {
      const styles = {
        transform: `rotateX(${25 * (index - activeIndex)}deg)`,
      };
      return (
        <div
          key={item.value || index}
          style={styles}
          className="PickerItem-item"
          ref={itemRef}
        >
          {format(item.label)}
        </div>
      );
    });
  }, [state.data]);

  const setPosition = (activeIndex: number, basedata?: any) => {
    const data: any = [...(basedata || state.data)];
    if (activeIndex >= data.length) {
      activeIndex = data.length - 1;
    }
    moveY.current = -activeIndex * itemHeight.current;
    setState({
      ...state,
      data,
      y: moveY.current,
      activeIndex,
    });
  };

  const touchStart = (event: any) => {
    if ((state.data as any[]).length === 0) {
      return;
    }
    if (props.disabled || !wrapper.current) {
      return;
    }
    dragging.current = true;
    const touch = isMobile ? event.changedTouches[0] : event;
    startY.current = touch.pageY;
    setTransitionDuration(
      (wrapper.current as HTMLDivElement).firstElementChild,
      0
    );
    document.addEventListener(isMobile ? "touchmove" : "mousemove", touchMove);
    document.addEventListener(isMobile ? "touchend" : "mouseup", touchEnd);
  };

  const touchMove = (event: any) => {
    if (dragging.current) {
      if ((state.data as any[]).length === 0) {
        return;
      }
      const touch = isMobile ? event.changedTouches[0] : event;
      const eY = touch.pageY;
      moveY.current += eY - startY.current;
      setState({
        ...state,
        y: moveY.current,
      });
      startY.current = eY;
    }
  };

  const touchEnd = () => {
    if (dragging.current && wrapper.current) {
      const scroll = (wrapper.current as HTMLDivElement).firstElementChild;
      setTransitionDuration(scroll, 300);
      const length: any = [...state.data!].length;
      if (length === 0) {
        return;
      }
      const data: any = state.data;
      let y: any = moveY.current;
      y = Math.max(Math.min(y, 0), -itemHeight.current * (length - 1));
      const activeIndex: number = Math.floor(Math.abs(y / itemHeight.current));
      moveY.current = -activeIndex * itemHeight.current;
      setState({
        ...state,
        y: moveY.current,
        activeIndex,
      });
      if (props.onValueChange) {
        props.onValueChange({
          activeIndex,
          index: props.index!,
          value: data[activeIndex],
        });
      }
      startY.current = 0;
    }
    dragging.current = false;
    document.removeEventListener(
      isMobile ? "touchmove" : "mousemove",
      touchMove
    );
    document.removeEventListener(isMobile ? "touchend" : "mouseup", touchEnd);
  };

  useEffect(() => {
    if (itemRef.current && wrapper.current) {
      itemHeight.current = (itemRef.current as HTMLDivElement).offsetHeight;
      (wrapper.current as HTMLDivElement).style.height =
        itemHeight.current * 3 + "px";
      setPosition(props.value);
    }
  }, []);

  useEffect(() => {
    setPosition(props.value, [...props.data]);
  }, [props.data, props.value]);

  return (
    <div className={styleClass} {...otherProps}>
      {dataProps.key ? (
        <div className="PickerItem-label">{dataProps.key}</div>
      ) : null}
      <div className="PickerItem-wrapper" ref={wrapper}>
        <div
          className="PickerItem-scroll"
          style={{
            transform: `translate3d(0, ${state.y}px, 0)`,
          }}
        >
          {getMenusItem()}
        </div>
        <div
          className="PickerItem-overlay"
          onTouchStart={touchStart}
          onMouseDown={
            isMobile
              ? () => {
                  return;
                }
              : touchStart
          }
        />
      </div>
    </div>
  );
}
